import React, { useContext, useEffect, useMemo, useRef } from "react";
import { createInstance, OptimizelyProvider, useDecision } from "@optimizely/react-sdk";
import { useAuth0 } from "@auth0/auth0-react";
import { IndividualLoginAuth0Context } from "@providers";
import { Center, Loader } from "@mantine/core";
import { useLocation } from "react-router-dom";
import { OptimizelyUserContext } from "./OptimizelyUserContext";
import { OnboardingStatusContext } from "@pages/onboarding";
import { OnboardingState } from "@pages/onboarding/types";

const MigrationCheck = ({
  children,
  orgId,
  sellerId,
}: {
  children: React.ReactNode;
  orgId?: string;
  sellerId?: number;
}) => {
  const runtimeConfig = window?.__RUNTIME_CONFIG__ || {};
  const hasChecked = useRef(false);
  const { search } = useLocation();
  const { onboardingStatusModel } = useContext(OnboardingStatusContext);

  const [decision, clientReady] = useDecision("new_seller_portal_rollout", {
    autoUpdate: true,
  });

  // Two cases for invitation flows:
  // 1. User already authenticated → arrives at /login?invitation=... → URL check catches it
  //    (MigrationCheck blocks children, so Login never runs, sessionStorage flag never set)
  // 2. User not authenticated → Login sets sessionStorage flag → Auth0 redirect → callback
  //    → now at /dashboard with no invitation in URL → sessionStorage check catches it
  const hasInvitationInUrl = new URLSearchParams(search).has("invitation");
  const hasPendingInvitationStorage = sessionStorage.getItem("pendingInvitationFlow") === "true";
  const isInvitationFlow = hasInvitationInUrl || hasPendingInvitationStorage;

  useEffect(() => {
    if (!clientReady || hasChecked.current) return;

    if (isInvitationFlow) {
      hasChecked.current = true;
      sessionStorage.removeItem("pendingInvitationFlow");
      return;
    }

    if (!onboardingStatusModel.Loaded) return;

    const isOnboardingComplete = onboardingStatusModel.OnboardingState === OnboardingState.Completed;
    const shouldRedirectToNewPortal = sellerId != null && decision.enabled && isOnboardingComplete;

    hasChecked.current = true;

    if (runtimeConfig.REACT_APP_NEW_SELLER_PORTAL_URL && shouldRedirectToNewPortal) {
      const baseUrl = runtimeConfig.REACT_APP_NEW_SELLER_PORTAL_URL.replace(/\/$/, "");
      const returnTo = window.location.pathname + window.location.search;
      const params = new URLSearchParams();
      if (orgId) params.set("orgId", orgId);
      params.set("returnTo", returnTo);
      window.location.href = `${baseUrl}/auth/login?${params.toString()}`;
    }
  }, [
    clientReady,
    decision.enabled,
    isInvitationFlow,
    onboardingStatusModel.Loaded,
    onboardingStatusModel.OnboardingState,
    orgId,
    runtimeConfig.REACT_APP_NEW_SELLER_PORTAL_URL,
    sellerId,
  ]);

  // Block rendering until we know the decision
  // Once checked, never block again (hasChecked prevents re-blocking on navigation)
  // Never block for invitation flows — Login must render to call loginWithRedirect
  if (!isInvitationFlow && !clientReady) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }

  return <>{children}</>;
};

export const OptimizelyContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth0(IndividualLoginAuth0Context);
  const runtimeConfig = window?.__RUNTIME_CONFIG__ || {};
  const sdkKey = runtimeConfig.REACT_APP_OPTIMIZELY_SDK_KEY;
  const pollingInterval = runtimeConfig.REACT_APP_OPTIMIZELY_POLLING_INTERVAL
    ? parseInt(runtimeConfig.REACT_APP_OPTIMIZELY_POLLING_INTERVAL, 10)
    : 300000;

  const userId = user?.sub;
  const sellerId = user?.["https://mydeal.com.au/company-id"];
  const orgId = user?.org_id as string | undefined;

  const userAttributes = useMemo(() => {
    const attrs: { [key: string]: any } = {};
    if (sellerId) {
      attrs.sellerID = Number(sellerId);
    }
    return attrs;
  }, [sellerId]);

  const optimizelyUserContext = useMemo(() => ({ userId, attributes: userAttributes }), [userId, userAttributes]);

  const optimizely = useMemo(() => {
    if (!sdkKey) return null;
    return createInstance({
      sdkKey,
      datafileOptions: { updateInterval: pollingInterval },
      eventBatchSize: 10,
      eventFlushInterval: 1000,
    });
  }, [sdkKey, pollingInterval]);

  if (!optimizely) {
    return <>{children}</>;
  }

  if (!isAuthenticated || isLoading || !userId || !sellerId) {
    return <OptimizelyUserContext.Provider value={optimizelyUserContext}>{children}</OptimizelyUserContext.Provider>;
  }

  const optimizelyUser = {
    id: userId as string,
    attributes: userAttributes,
  };

  return (
    <OptimizelyProvider optimizely={optimizely} user={optimizelyUser} timeout={500}>
      <OptimizelyUserContext.Provider value={optimizelyUserContext}>
        <MigrationCheck orgId={orgId} sellerId={sellerId}>
          {children}
        </MigrationCheck>
      </OptimizelyUserContext.Provider>
    </OptimizelyProvider>
  );
};
